import { Table } from 'react-bootstrap';
import { BonusAssignmentModel, getEmployeeFullName } from '../models/bonusAssignmentModel';
import { useContext } from 'react';
import { AppContext } from '../../../common';

interface BonusTableProps {
    data: Record<string, BonusAssignmentModel[]>;
    selectedList: BonusAssignmentModel[];
    handleChangeSelected: (item: BonusAssignmentModel) => void;
    groupBy?: 'employee' | 'date';
}

const BonusTable = ({
    data,
    selectedList,
    handleChangeSelected,
    groupBy = 'employee',
}: BonusTableProps) => {
    const { activeSkinConfig, langResources } = useContext(AppContext);

    if (Object.keys(data).length === 0) {
        return (
            <div className="text-center text-muted my-5">
                {langResources['bonus']?.table?.noData || 'No hay bonificaciones para mostrar'}
            </div>
        );
    }

    const sortedData = Object.keys(data)
        .sort((a, b) => {
            if (groupBy === 'employee') {
                return a.localeCompare(b);
            } else {
                return new Date(a).getTime() - new Date(b).getTime();
            }
        })
        .reduce((acc, key) => {
            if (groupBy === 'date') {
                acc[key] = data[key].sort((a, b) => {
                    const nameA = getEmployeeFullName(a.employee);
                    const nameB = getEmployeeFullName(b.employee);
                    return nameA.localeCompare(nameB);
                });
            } else {
                acc[key] = data[key].sort((a, b) => {
                    // First compare by date
                    const dateCompare = new Date(a.date).getTime() - new Date(b.date).getTime();
                    if (dateCompare !== 0) {
                        return dateCompare;
                    }

                    // If same date, sort by bonus rule name alphabetically
                    return a.bonusRule.name.localeCompare(b.bonusRule.name);
                });
            }
            return acc;
        }, {} as Record<string, BonusAssignmentModel[]>);

    const handleSelectAllGroup = (groupKey: string, isChecked: boolean) => {
        const groupItems = sortedData[groupKey];
        
        if (isChecked) {
            groupItems.forEach((item) => {
                if (!selectedList.includes(item)) {
                    handleChangeSelected(item);
                }
            });
        } else {
            groupItems.forEach((item) => {
                if (selectedList.includes(item)) {
                    handleChangeSelected(item);
                }
            });
        }
    };

    const isGroupAllSelected = (groupKey: string) => {
        const groupItems = sortedData[groupKey];
        return groupItems.every((item) => selectedList.includes(item));
    };

    const isGroupIndeterminate = (groupKey: string) => {
        const groupItems = sortedData[groupKey];
        const selectedCount = groupItems.filter((item) => selectedList.includes(item)).length;
        return selectedCount > 0 && selectedCount < groupItems.length;
    };

    return (
        <div>
            {Object.keys(sortedData).map((groupKey) => (
                <div key={groupKey} className='mb-4'>
                    <h5 className='mb-3'>
                        {groupBy === 'employee'
                            ? `${getEmployeeFullName(sortedData[groupKey][0].employee)} - ${sortedData[groupKey][0].employee.documentNumber}`
                            : new Date(groupKey).toISOString().slice(0, 10)}
                    </h5>
                    <Table striped bordered hover responsive style={{ tableLayout: 'fixed' }}>
                        <thead style={{ backgroundColor: `${activeSkinConfig.principalColor}15` }}>
                            <tr>
                                <th style={{ width: '50px' }}>
                                    <input
                                        type='checkbox'
                                        className='form-check-input'
                                        checked={isGroupAllSelected(groupKey)}
                                        ref={(el) => {
                                            if (el) {
                                                el.indeterminate = isGroupIndeterminate(groupKey);
                                            }
                                        }}
                                        onChange={(e) =>
                                            handleSelectAllGroup(groupKey, e.target.checked)
                                        }
                                        style={{
                                            borderColor: activeSkinConfig.principalColor,
                                            ...(isGroupAllSelected(groupKey) && {
                                                backgroundColor: activeSkinConfig.principalColor,
                                                borderColor: activeSkinConfig.principalColor
                                            })
                                        }}
                                        title='seleccionar todo'
                                    />
                                </th>
                                {groupBy === 'date' && <th>{langResources['bonus']['table']['employee'] || 'Empleado'}</th>}
                                <th>{langResources['bonus']['table']['bonus.rule']}</th>
                                <th>{langResources['bonus']['table']['allowance']}</th>
                                <th>{langResources['bonus']['table']['date']}</th>
                                <th>{langResources['bonus']['table']['amount']}</th>
                                <th>{langResources['created.by.label']}</th>
                                <th>{langResources['created.date.label']}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {sortedData[groupKey].map((item) => (
                                <tr key={item.id}>
                                    <td>
                                        <input
                                            type='checkbox'
                                            className='form-check-input'
                                            checked={selectedList.includes(item)}
                                            onChange={() => handleChangeSelected(item)}
                                            style={{
                                                borderColor: activeSkinConfig.principalColor,
                                                ...(selectedList.includes(item) && {
                                                    backgroundColor: activeSkinConfig.principalColor,
                                                    borderColor: activeSkinConfig.principalColor
                                                })
                                            }}
                                        />
                                    </td>
                                    {groupBy === 'date' && (
                                        <td>{getEmployeeFullName(item.employee)}</td>
                                    )}
                                    <td>{item.bonusRule.name}</td>
                                    <td>{item.bonusRule.allowance.name}</td>
                                    <td>
                                        {new Date(item.date).toLocaleDateString('es-CO')}
                                    </td>
                                    <td>{item.amount}</td>
                                    <td>
                                        {item.createdBy.name} {item.createdBy.lastName}
                                    </td>
                                    <td>
                                        {new Date(item.createdDate).toLocaleString('es-CO')}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </div>
            ))}
        </div>
    );
};

export default BonusTable;